import { RootState } from "../index";
import { createEntityAdapter, createSlice, EntityId } from '@reduxjs/toolkit';
import { fetchUser, fetchUsers, updateUser, blockUser } from "../../actions/roblox/usersActions";
import { fetchComment, fetchComments, fetchReplies } from "../../actions/roblox/commentsActions";
import { fetchSkin, fetchSkins } from "../../actions/roblox/skinsActions";
import { fetchReport, fetchReports } from "../../actions/roblox/reportsActions";

export interface UserInterface {
  url: string;

  id: number;
  name: string;
  email: string;
  coins: number;
  avatar_url: string;
  created_at: string;
  updated_at: string;
  // block | mute
  banned: boolean;
  banned_until: string;
  muted: boolean;
  muted_until: string;
  // ads
  native: boolean;
  banner: boolean;
  app_open: boolean;
  interstitial: boolean;
  interstitial_long: boolean;

  launch_count: number;
  launch_at: string;
  last_launch_at: string;

  popular_count: number;
  following_count: number;
  followers_count: number;
  comments_count: number;
  skins_count: number;
  likes_count: number;
  is_online: boolean,

  bundle?: {
    id: EntityId;
    name: string;
  };
}

export interface initialInterface {
  error: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  count: number;

  editId: EntityId | null;
  editError: string | null;

  banId: EntityId | null;
  banError: string | null;
}

const initialState: initialInterface = {
  error: null,
  status: 'idle',
  count: 0,

  editId: null,
  editError: null,

  banId: null,
  banError: null,
};

const usersAdapter = createEntityAdapter<UserInterface>();

const slice = createSlice({
  name: 'users',
  initialState: usersAdapter.getInitialState(initialState),
  reducers: {
    setEditId: (state, action) => {
      state.editId    = action.payload;
      state.editError = initialState.editError;
    },
    setEditError: (state, action) => {
      state.editError = action.payload;
    },
    setBanId: (state, action) => {
      state.banId    = action.payload;
      state.banError = initialState.banError;
    },
    setBanError: (state, action) => {
      state.banError = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // #index
      .addCase(fetchUsers.pending, (state, _action) => {
        state.status = 'loading'
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.count = action.payload.count;
        state.entities = action.payload.users;
        state.ids = action.payload.ids;
        state.status = 'succeeded';
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Unknown Error';
      })

      // #show
      .addCase(fetchUser.fulfilled, (state, action) => {
        const user = Object.values(action.payload.user)[0];
        state.entities[user.id] = user;
      })

      // #update
      .addCase(updateUser.fulfilled, (state, action) => {
        const user = Object.values(action.payload.user)[0];
        state.entities[user.id] = user;
      })
      .addCase(updateUser.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.editError = error || action.error.message || 'Unknown Error';
      })

      // #skins
      .addCase(fetchSkins.fulfilled, (state, action) => {
        state.entities = { ...state.entities, ...action.payload.users };
      })
      .addCase(fetchSkin.fulfilled, (state, action) => {
        state.entities = { ...state.entities, ...action.payload.user };
      })

      // #reports
      .addCase(fetchReports.fulfilled, (state, action) => {
        state.entities = { ...state.entities, ...action.payload.users, ...action.payload.active_users };
      })
      .addCase(fetchReport.fulfilled, (state, action) => {
        state.entities = { ...state.entities, ...action.payload.user, ...action.payload.active_user };
      })

      // #ban
      .addCase(blockUser.fulfilled, (state, action) => {
        const user = Object.values(action.payload.user)[0];
        state.entities[user.id] = user;
      })
      .addCase(blockUser.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.banError = error || action.error.message || 'Unknown Error';
      })

      // #comments
      .addCase(fetchComments.fulfilled, (state, action) => {
        state.entities = { ...state.entities, ...action.payload.users };
      })
      .addCase(fetchReplies.fulfilled, (state, action) => {
        state.entities = { ...state.entities, ...action.payload.users };
      })
      .addCase(fetchComment.fulfilled, (state, action) => {
        state.entities = { ...state.entities, ...action.payload.user };
      })
  }
});

const reducer = slice.reducer;
export default reducer;

export const {
  setEditId,
  setEditError,
  setBanId,
  setBanError
} = slice.actions;

export const {
  selectById: selectUserById,
  selectIds: selectUserIds,
  selectEntities: selectUserEntities
} = usersAdapter.getSelectors<RootState>(state => state.roblox.users);
